(function () {
  "use strict";

  function initPriceHistory(root) {
    if (root.dataset.phInitialized === "true") return;
    root.dataset.phInitialized = "true";

    const apiBase = root.dataset.apiBase;
    const canvas = root.querySelector(".price-history__canvas");
    const tooltip = root.querySelector(".price-history__tooltip");
    const tooltipDate = root.querySelector(".price-history__tooltip-date");
    const tooltipPrice = root.querySelector(".price-history__tooltip-price");
    const status = root.querySelector(".price-history__status");
    const tabs = Array.from(root.querySelectorAll(".price-history__tabs button"));
    const ctx = canvas.getContext("2d");

    let currentVariantId = root.dataset.variantId;
    let currentPrice = parseFloat(root.dataset.currentPrice || "0") || 0;
    let currentRange = "1J";
    let allPoints = []; // [{date: Date, price: Number}]
    let visiblePoints = [];
    let layout = null;
    let hoverIndex = null;
    const cache = new Map(); // variantId -> points

    function setStatus(text) {
      if (!text) {
        status.hidden = true;
        status.textContent = "";
      } else {
        status.hidden = false;
        status.textContent = text;
      }
    }

    function styleVar(name) {
      return getComputedStyle(root).getPropertyValue(name).trim();
    }

    function formatDate(d) {
      const locale = document.documentElement.lang || "de-CH";
      return d.toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" });
    }

    function formatPrice(p) {
      return "CHF " + p.toFixed(2);
    }

    function formatPriceShort(p) {
      const rounded = Math.max(0, Math.round(p));
      return rounded.toString();
    }

    function filterRange(range) {
      if (allPoints.length === 0) return [];
      if (range === "MAX") return allPoints;
      const last = allPoints[allPoints.length - 1].date;
      const months = { "1M": 1, "3M": 3, "6M": 6, "1J": 12 }[range];
      const cutoff = new Date(last);
      cutoff.setMonth(cutoff.getMonth() - months);
      const pts = allPoints.filter((p) => p.date >= cutoff);
      return pts.length >= 1 ? pts : allPoints.slice(-1);
    }

    function draw() {
      if (visiblePoints.length === 0) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const w = rect.width;
      const h = rect.height;
      const padL = 44, padR = 8, padT = 16, padB = 22;

      const prices = visiblePoints.map((p) => p.price);
      let min = Math.min(...prices);
      let max = Math.max(...prices);
      if (min === max) { min -= Math.max(min * 0.15, 5); max += Math.max(max * 0.15, 5); }
      if (min > 0) min = Math.max(0, min - (max - min) * 0.12);
      const spread = max - min;
      max += spread * 0.15;

      const denom = Math.max(visiblePoints.length - 1, 1);
      const x = (i) => padL + (i / denom) * (w - padL - padR);
      const y = (v) => padT + (1 - (v - min) / (max - min)) * (h - padT - padB);

      ctx.clearRect(0, 0, w, h);

      ctx.strokeStyle = styleVar("--ph-border");
      ctx.lineWidth = 1;
      const gridLines = 3;
      ctx.save();
      ctx.font = "600 10px -apple-system, BlinkMacSystemFont, sans-serif";
      ctx.fillStyle = styleVar("--ph-axis");
      ctx.textBaseline = "middle";
      ctx.textAlign = "right";
      for (let g = 0; g <= gridLines; g++) {
        const gy = padT + (g / gridLines) * (h - padT - padB);
        const gv = max - (g / gridLines) * (max - min);
        ctx.beginPath();
        ctx.moveTo(padL, gy + 0.5);
        ctx.lineTo(w - padR, gy + 0.5);
        ctx.stroke();
        ctx.fillText(formatPriceShort(gv), padL - 8, gy);
      }
      ctx.restore();

      const accent = styleVar("--ph-accent");
      const accent2 = styleVar("--ph-accent-2");

      if (visiblePoints.length > 1) {
        const grad = ctx.createLinearGradient(0, padT, 0, h - padB);
        grad.addColorStop(0, accent2 + "55");
        grad.addColorStop(1, accent2 + "00");

        ctx.beginPath();
        visiblePoints.forEach((p, i) => {
          const px = x(i), py = y(p.price);
          if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        });
        ctx.lineTo(x(visiblePoints.length - 1), h - padB);
        ctx.lineTo(x(0), h - padB);
        ctx.closePath();
        ctx.fillStyle = grad;
        ctx.fill();

        ctx.beginPath();
        visiblePoints.forEach((p, i) => {
          const px = x(i), py = y(p.price);
          if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        });
        ctx.strokeStyle = accent;
        ctx.lineWidth = 2.2;
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.stroke();
      }

      const lastI = visiblePoints.length - 1;
      ctx.save();
      ctx.shadowColor = accent2;
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(x(lastI), y(visiblePoints[lastI].price), 3.6, 0, Math.PI * 2);
      ctx.fillStyle = accent2;
      ctx.fill();
      ctx.restore();

      if (hoverIndex !== null && visiblePoints[hoverIndex]) {
        const hp = visiblePoints[hoverIndex];
        const hx = x(hoverIndex);
        const hy = y(hp.price);

        ctx.save();
        ctx.shadowColor = accent2;
        ctx.shadowBlur = 14;
        ctx.strokeStyle = accent2 + "aa";
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(hx, padT);
        ctx.lineTo(hx, h - padB);
        ctx.stroke();

        ctx.shadowBlur = 0;
        ctx.strokeStyle = accent2;
        ctx.lineWidth = 0.75;
        ctx.beginPath();
        ctx.moveTo(hx, padT);
        ctx.lineTo(hx, h - padB);
        ctx.stroke();
        ctx.restore();

        ctx.save();
        ctx.shadowColor = accent2;
        ctx.shadowBlur = 18;
        ctx.beginPath();
        ctx.arc(hx, hy, 5, 0, Math.PI * 2);
        ctx.fillStyle = accent2;
        ctx.fill();
        ctx.restore();

        ctx.beginPath();
        ctx.arc(hx, hy, 2.2, 0, Math.PI * 2);
        ctx.fillStyle = "#fff";
        ctx.fill();
      }

      ctx.save();
      ctx.fillStyle = styleVar("--ph-axis");
      ctx.font = "600 11px -apple-system, BlinkMacSystemFont, sans-serif";
      ctx.textBaseline = "top";
      ctx.shadowColor = "rgba(0, 0, 0, 0.65)";
      ctx.shadowBlur = 4;
      ctx.shadowOffsetY = 1;
      ctx.textAlign = "left";
      ctx.fillText(formatDate(visiblePoints[0].date), padL, h - padB + 6);
      ctx.textAlign = "right";
      ctx.fillText(formatDate(visiblePoints[lastI].date), w - padR, h - padB + 6);
      ctx.restore();

      layout = { x, y, w, h, padL, padR };
      placeLabel(hoverIndex !== null ? hoverIndex : visiblePoints.length - 1, hoverIndex !== null);
    }

    function nearestIndex(mouseX) {
      if (!layout) return 0;
      let best = 0, bestDist = Infinity;
      visiblePoints.forEach((p, i) => {
        const d = Math.abs(layout.x(i) - mouseX);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      return best;
    }

    // Zeigt ein Label mit Datum + Preis am angegebenen Punkt. Ohne Hover
    // bleibt das Label permanent am aktuellsten Punkt sichtbar (nicht nur
    // bei :hover), damit sofort erkennbar ist, wo der Kurs gerade steht.
    function placeLabel(i, isHover) {
      if (!visiblePoints[i] || !layout) return;
      const p = visiblePoints[i];
      const isFallback = allPoints.length === 0 && !isHover;
      tooltipDate.textContent = isFallback ? (root.dataset.currentLabel || formatDate(p.date)) : formatDate(p.date);
      tooltipPrice.textContent = formatPrice(p.price);
      tooltip.classList.add("is-visible");
      tooltip.classList.toggle("is-pinned", !isHover);
      const rect = canvas.getBoundingClientRect();
      let left = layout.x(i) - 40;
      left = Math.max(4, Math.min(rect.width - 100, left));
      tooltip.style.left = left + "px";
      tooltip.style.top = Math.max(4, layout.y(p.price) - 54) + "px";
    }

    function showTooltip(i) {
      if (!visiblePoints[i]) return;
      hoverIndex = i;
      draw();
    }

    function hideTooltip() {
      hoverIndex = null;
      draw();
    }

    canvas.addEventListener("mousemove", (e) => {
      if (visiblePoints.length < 1) return;
      const rect = canvas.getBoundingClientRect();
      showTooltip(nearestIndex(e.clientX - rect.left));
    });
    canvas.addEventListener("mouseleave", hideTooltip);
    canvas.addEventListener("touchstart", (e) => {
      if (visiblePoints.length < 1) return;
      const rect = canvas.getBoundingClientRect();
      showTooltip(nearestIndex(e.touches[0].clientX - rect.left));
    }, { passive: true });
    canvas.addEventListener("touchmove", (e) => {
      if (visiblePoints.length < 1) return;
      const rect = canvas.getBoundingClientRect();
      showTooltip(nearestIndex(e.touches[0].clientX - rect.left));
    }, { passive: true });
    canvas.addEventListener("touchend", hideTooltip);

    tabs.forEach((btn) => {
      btn.addEventListener("click", () => {
        tabs.forEach((b) => b.setAttribute("aria-pressed", "false"));
        btn.setAttribute("aria-pressed", "true");
        currentRange = btn.dataset.range;
        hoverIndex = null;
        applyRange();
      });
    });

    function singlePointText(point) {
      const tpl = root.dataset.singlePointLabel || "";
      return tpl
        .replace("%DATE%", formatDate(point.date))
        .replace("%PRICE%", formatPrice(point.price));
    }

    function applyRange() {
      visiblePoints = filterRange(currentRange);

      // Kein gespeicherter Punkt ueberhaupt (weder Historie noch Fallback) ->
      // "keine Preisentwicklung"-Hinweis, keine Grafik.
      if (visiblePoints.length === 0 && allPoints.length === 0 && currentPrice <= 0) {
        canvas.hidden = true;
        setStatus(root.dataset.emptyLabel || "");
        return;
      }

      // Genau EIN echter, gespeicherter Datenpunkt: Text statt (potenziell
      // irrefuehrender) flacher Linie anzeigen.
      if (allPoints.length === 1) {
        canvas.hidden = true;
        tooltip.classList.remove("is-visible");
        setStatus(singlePointText(allPoints[0]));
        return;
      }

      if (visiblePoints.length === 0) {
        // Noch keine gespeicherte Preishistorie ueberhaupt: zeige den aktuell
        // angezeigten Verkaufspreis als Text, keine Grafik.
        if (currentPrice > 0) {
          canvas.hidden = true;
          tooltip.classList.remove("is-visible");
          setStatus(singlePointText({ date: new Date(), price: currentPrice }));
          return;
        }
        canvas.hidden = true;
        setStatus(root.dataset.emptyLabel || "");
        return;
      }

      if (visiblePoints.length === 1) {
        // Gewaehlter Zeitraum enthaelt nur den letzten bekannten Punkt vor dem
        // Cutoff (siehe Worker-Fallback) -> ebenfalls als Text, nicht als Linie.
        canvas.hidden = true;
        tooltip.classList.remove("is-visible");
        setStatus(singlePointText(visiblePoints[0]));
        return;
      }

      canvas.hidden = false;
      setStatus(null);
      draw();
    }

    async function loadVariant(variantId, variantPrice) {
      currentVariantId = variantId;
      if (typeof variantPrice === "number" && variantPrice > 0) currentPrice = variantPrice;
      setStatus(root.dataset.loadingLabel || "");
      tooltip.classList.remove("is-visible");

      if (cache.has(variantId)) {
        allPoints = cache.get(variantId);
        applyRange();
        return;
      }

      try {
        const url = `${apiBase}/price-history?variant_id=${encodeURIComponent(variantId)}&range=MAX`;
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error("bad response");
        const json = await res.json();
        const points = (json.points || []).map((p) => ({
          date: new Date(p.changed_at),
          price: parseFloat(p.price),
        }));
        cache.set(variantId, points);
        allPoints = points;
        applyRange();
      } catch (err) {
        allPoints = [];
        applyRange();
      }
    }

    window.addEventListener("resize", () => draw());

    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event && event.data && event.data.variant;
        const sectionId = event && event.data && event.data.sectionId;
        if (!variant || !variant.id) return;
        if (sectionId && root.dataset.sectionId && sectionId !== root.dataset.sectionId) return;
        if (String(variant.id) === String(currentVariantId)) return;
        const variantPrice = typeof variant.price === "number" ? variant.price / 100 : undefined;
        loadVariant(variant.id, variantPrice);
      });
    }

    loadVariant(currentVariantId);
  }

  function boot() {
    document.querySelectorAll("[data-price-history]").forEach((root) => {
      root.dataset.loadingLabel = root.dataset.loadingLabel || "";
      initPriceHistory(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
